/**
 * @author Gananath Athukorala
 * @version 0.1
 */

import React, { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { MaterialReactTable, useMaterialReactTable } from "material-react-table"
import api from "../services/AuthApi"
import labelsEN from "../../../locales/en/translation.json"
import { Box, Fab, IconButton, Tooltip } from "@mui/material"
import LockResetIcon from "@mui/icons-material/LockReset"
import AccountCircleIcon from "@mui/icons-material/AccountCircle"
import ResetPasswordModal from "./components/ResetPasswordModal"

const CredentialManager = () => {
  // data and fetching state
  const [appData, setAppData] = useState([])
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [isAppRefetching, setIsAppRefetching] = useState(false)
  const [appRowCount, setAppRowCount] = useState(0)

  //table state
  const [columnFilters, setColumnFilters] = useState([])
  const [globalFilter, setGlobalFilter] = useState("")
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  })

  //modals state
  const [updateModalOpen, setUpdateModalOpen] = useState(false)
  const [rowData, setRowData] = useState([])

  // Approved User Accounts
  let getAppConfig = {
    headers: {
      "Sort-Order": "ASC",
      Action: "VAU",
    },
  }

  const approveTable = () => {
    api
      .get(
        "/users/" +
          pagination.pageIndex +
          "/" +
          pagination.pageSize +
          "/userId",
        getAppConfig
      )
      .then((res) => {
        setAppData(res.data?.dataList)
        setAppRowCount(res.data?.total)
        setIsLoading(false)
      })
      .catch((error) => {
        console.log(error)
      })
    setIsError(false)
    setIsAppRefetching(false)
  }

  useEffect(() => {
    approveTable()
  }, [pagination.pageIndex, pagination.pageSize])

  const columns = useMemo(
    () => [
      {
        accessorKey: "username",
        header: "Username",
      },
      {
        accessorKey: "firstName",
        header: "First Name",
      },
      {
        accessorKey: "lastName",
        header: "Last Name",
      },
      {
        accessorKey: "primEmail",
        header: "Prim Email",
      },
      {
        accessorKey: "primContact",
        header: "Prim Contact",
      },
      {
        accessorKey: "userRole",
        header: "User Role",
      },
    ],
    []
  )

  const navigate = useNavigate()
  const handleNavigate = () => {
    navigate("../../app/userAccounts")
  }

  return (
    <div>
      <Box sx={{ width: "100%" }}>
        <div style={{ display: "flex", position: "relative" }}>
          <h1>{labelsEN.app_credentialmanager_page_title}</h1>
          <Fab
            onClick={handleNavigate}
            color="success"
            variant="extended"
            sx={{ position: "absolute", right: 0 }}
          >
            <AccountCircleIcon sx={{ mr: 1 }} />
            User Accounts
          </Fab>
        </div>
        <br />
        <br />
        <MaterialReactTable
          enableColumnVirtualization
          columns={columns}
          data={appData ?? []}
          state={{
            columnFilters,
            globalFilter,
            isLoading,
            pagination,
            showProgressBars: isAppRefetching,
          }}
          getRowId={(row) => row.phoneNumber}
          initialState={{ showColumnFilters: false }}
          manualPagination
          muiToolbarAlertBannerProps={
            isError
              ? {
                  color: "error",
                  children: "Error loading data",
                }
              : undefined
          }
          onColumnFiltersChange={setColumnFilters}
          onGlobalFilterChange={setGlobalFilter}
          onPaginationChange={setPagination}
          rowCount={appRowCount}
          muiTablePaginationProps={{
            rowsPerPageOptions: [10],
          }}
          editingMode="modal"
          enableColumnOrdering
          enableGrouping
          enablePinning
          enableRowActions
          enableEditing
          renderRowActions={({ row }) => (
            <Box sx={{ display: "flex", gap: "1rem" }}>
              <Tooltip arrow placement="left" title="Reset Password">
                <IconButton
                  onClick={() => {
                    setUpdateModalOpen(true)
                    setRowData(row.original)
                  }}
                >
                  <LockResetIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        />
      </Box>

      <ResetPasswordModal
        username={rowData.username}
        open={updateModalOpen}
        onClose={() => {
          setUpdateModalOpen(false)
          approveTable()
        }}
      />
    </div>
  )
}

export default CredentialManager
